import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Grid,
  Chip,
  Alert,
  Card,
  CardContent,
  Divider,
  TextField,
  IconButton,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  DevicesOther as DevicesOtherIcon,
  Security as SecurityIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import './UserDetails.css';

// Create custom green icon for "Current Location" pins
const defaultIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Create custom green icon for "Current Location" pins
const greenIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

function UserDetails() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userLakes, setUserLakes] = useState(null);
  const [authData, setAuthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  
  // Email editing state
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailUpdateLoading, setEmailUpdateLoading] = useState(false);

  // Roles editing state
  const [isEditingRoles, setIsEditingRoles] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [rolesUpdateLoading, setRolesUpdateLoading] = useState(false);
  const availableRoles = ['Admin', 'Lake Manager'];

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    fetchUserDetails();
    fetchAuthDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch user details from the API
      const data = await api.post('/users/search', {
        searchTerm: userId,
        page: 1,
        limit: 1
      });
      const foundUser = data.users.find(u => u.id === userId);
      
      if (!foundUser) {
        throw new Error('User not found');
      }

      setUser(foundUser);

      // Fetch user lakes from Firestore
      try {
        const userLakesRef = doc(db, 'user-lakes', userId);
        const userLakesSnap = await getDoc(userLakesRef);
        
        if (userLakesSnap.exists()) {
          setUserLakes(userLakesSnap.data());
        } else {
          setUserLakes({ lakes: [] });
        }
      } catch (lakesError) {
        console.error('Error fetching user lakes:', lakesError);
        setUserLakes({ lakes: [] });
      }

    } catch (error) {
      console.error('Error fetching user details:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuthDetails = async () => {
    setAuthLoading(true);
    setAuthError(null);
    
    try {
      const authDetails = await api.get(`/users/auth/${userId}`);
      setAuthData(authDetails);
    } catch (error) {
      console.error('Error fetching auth details:', error);
      setAuthError(error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/users');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp.seconds * 1000).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  const formatAuthDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  const getMapCenter = () => {
    if (!userLakes?.lakes || userLakes.lakes.length === 0) {
      return [44.9778, -93.2650]; // Default to Minneapolis
    }

    const validLakes = userLakes.lakes.filter(lake => 
      lake.latitude && lake.longitude && 
      !isNaN(lake.latitude) && !isNaN(lake.longitude)
    );

    if (validLakes.length === 0) {
      return [44.9778, -93.2650];
    }

    // Center map on the first location in the lakes list
    const firstLake = validLakes[0];
    return [firstLake.latitude, firstLake.longitude];
  };

  const handleEditEmail = () => {
    setNewEmail(authData?.email || '');
    setIsEditingEmail(true);
  };

  const handleCancelEmailEdit = () => {
    setIsEditingEmail(false);
    setNewEmail('');
  };

  const handleSaveEmail = async () => {
    if (!newEmail.trim()) {
      setSnackbarMessage('Email cannot be empty');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setSnackbarMessage('Please enter a valid email address');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (newEmail === authData?.email) {
      setIsEditingEmail(false);
      return;
    }

    setEmailUpdateLoading(true);

    try {
      const updatedUser = await api.put(`/users/auth/${userId}/email`, {
        email: newEmail
      });

      // Update the local authData state
      setAuthData(prevData => ({
        ...prevData,
        email: updatedUser.email,
        emailVerified: updatedUser.emailVerified
      }));

      setIsEditingEmail(false);
      setSnackbarMessage('Email updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

    } catch (error) {
      console.error('Error updating email:', error);
      setSnackbarMessage(error.message || 'Failed to update email');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setEmailUpdateLoading(false);
    }
  };

  const handleEditRoles = () => {
    const currentRoles = authData?.customClaims?.roles
      || (authData?.customClaims?.roles ? authData.customClaims.roles : []);
    setSelectedRoles(currentRoles);
    setIsEditingRoles(true);
  };

  const handleCancelRolesEdit = () => {
    setIsEditingRoles(false);
    setSelectedRoles([]);
  };

  const handleSaveRoles = async () => {
    // Ensure array
    const rolesToSave = Array.isArray(selectedRoles) ? selectedRoles : [];
    setRolesUpdateLoading(true);

    try {
      const result = await api.put(`/users/auth/${userId}/roles`, { 
        roles: rolesToSave 
      });

      // Update local authData customClaims
      setAuthData(prev => ({
        ...prev,
        customClaims: result.customClaims || {}
      }));

      setIsEditingRoles(false);
      setSnackbarMessage('Roles updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error updating roles:', error);
      setSnackbarMessage(error.message || 'Failed to update roles');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setRolesUpdateLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Box className="loading-container">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="error-container">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" size="small" onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Back to Users
        </Button>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box className="error-container">
        <Alert severity="warning" sx={{ mb: 2 }}>
          User not found
        </Alert>
        <Button variant="contained" size="small" onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Back to Users
        </Button>
      </Box>
    );
  }

  const validLakes = userLakes?.lakes?.filter(lake => 
    lake.latitude && lake.longitude && 
    !isNaN(lake.latitude) && !isNaN(lake.longitude)
  ) || [];

  return (
    <Box className="user-details-container">
      <Button 
        variant="outlined" 
        size="small"
        onClick={handleBack} 
        startIcon={<ArrowBackIcon />}
        className="back-button"
      >
        Back to Users
      </Button>

      <Box className="user-details-header">
        <Typography variant="h4" component="h1" gutterBottom>
          User Details
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {user.displayName || 'Unnamed User'}
        </Typography>
      </Box>

      <Box className="user-details-content">
        {/* User Information Section */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon />
              User Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box className="info-item">
                  <Typography className="info-label">User ID</Typography>
                  <Typography className="info-value">{user.id}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box className="info-item">
                  <Typography className="info-label">Display Name</Typography>
                  <Typography className="info-value">{user.displayName || 'N/A'}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box className="info-item">
                  <Typography className="info-label">Email</Typography>
                  <Typography className="info-value">{user.email || 'N/A'}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box className="info-item">
                  <Typography className="info-label">Contact ID</Typography>
                  <Typography className="info-value">{user.contactId || 'N/A'}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box className="info-item">
                  <Typography className="info-label">Created</Typography>
                  <Typography className="info-value">{formatDate(user.metadata?.creationTime)}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box className="info-item">
                  <Typography className="info-label">Last Sign In</Typography>
                  <Typography className="info-value">{formatDate(user.metadata?.lastSignInTime)}</Typography>
                </Box>
              </Grid>
              
              {user.platform && (
                <>
                  <Grid item xs={12} md={6}>
                    <Box className="info-item">
                      <Typography className="info-label">Platform</Typography>
                      <Typography className="info-value">{user.platform.os || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box className="info-item">
                      <Typography className="info-label">App Version</Typography>
                      <Typography className="info-value">{user.platform.appVersion || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                </>
              )}
              
              {user.featureFlags && Object.keys(user.featureFlags).length > 0 && (
                <Grid item xs={12}>
                  <Box className="info-item">
                    <Typography className="info-label">Feature Flags</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {Object.entries(user.featureFlags).map(([flag, enabled]) => (
                        <Chip 
                          key={flag}
                          label={`${flag}: ${enabled ? 'ON' : 'OFF'}`}
                          color={enabled ? 'success' : 'default'}
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Authentication Section */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon />
              Authentication
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {authLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : authError ? (
              <Alert severity="warning">
                {authError}
              </Alert>
            ) : authData ? (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box className="info-item">
                    <Typography className="info-label">UID</Typography>
                    <Typography className="info-value">{authData.uid}</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box className="info-item">
                    <Typography className="info-label">Email</Typography>
                    {isEditingEmail ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          size="small"
                          variant="outlined"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          disabled={emailUpdateLoading}
                          sx={{ flex: 1 }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveEmail();
                            }
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={handleSaveEmail}
                          disabled={emailUpdateLoading}
                          sx={{ ml: 1, width: 'auto' }}
                          color="primary"
                        >
                          {emailUpdateLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SaveIcon />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelEmailEdit}
                          disabled={emailUpdateLoading}
                            sx={{ ml: 1, width: 'auto' }}
                          color="default"
                        >
                          <CancelIcon />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography className="info-value">{authData.email || 'N/A'}</Typography>
                        {authData.email && (
                          <IconButton
                            size="small"
                            onClick={handleEditEmail}
                            sx={{ ml: 1, width: 'auto' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box className="info-item">
                    <Typography className="info-label">Email Verified</Typography>
                    <Chip 
                      label={authData.emailVerified ? 'Verified' : 'Not Verified'}
                      color={authData.emailVerified ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box className="info-item">
                    <Typography className="info-label">Account Status</Typography>
                    <Chip 
                      label={authData.disabled ? 'Disabled' : 'Active'}
                      color={authData.disabled ? 'error' : 'success'}
                      size="small"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box className="info-item">
                    <Typography className="info-label">Display Name</Typography>
                    <Typography className="info-value">{authData.displayName || 'N/A'}</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box className="info-item">
                    <Typography className="info-label">Phone Number</Typography>
                    <Typography className="info-value">{authData.phoneNumber || 'N/A'}</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box className="info-item">
                    <Typography className="info-label">Created</Typography>
                    <Typography className="info-value">{formatAuthDate(authData.metadata?.creationTime)}</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box className="info-item">
                    <Typography className="info-label">Last Sign In</Typography>
                    <Typography className="info-value">{formatAuthDate(authData.metadata?.lastSignInTime)}</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box className="info-item">
                    <Typography className="info-label">Last Refresh</Typography>
                    <Typography className="info-value">{formatAuthDate(authData.metadata?.lastRefreshTime)}</Typography>
                  </Box>
                </Grid>
                
                {authData.providerData && authData.providerData.length > 0 && (
                  <Grid item xs={12}>
                    <Box className="info-item">
                      <Typography className="info-label">Sign-in Providers</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {authData.providerData.map((provider, index) => (
                          <Chip 
                            key={index}
                            label={provider.providerId}
                            color="primary"
                            size="small"
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                )}
                
                {/* Roles editor */}
                <Grid item xs={12}>
                  <Box className="info-item">
                    <Typography className="info-label">Roles</Typography>
                    {isEditingRoles ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <FormControl sx={{ minWidth: 240 }}>
                          <InputLabel id="roles-select-label">Roles</InputLabel>
                          <Select
                            labelId="roles-select-label"
                            multiple
                            value={selectedRoles}
                            onChange={(e) => setSelectedRoles(e.target.value)}
                            input={<OutlinedInput label="Roles" />}
                            renderValue={(selected) => selected.join(', ')}
                            size="small"
                          >
                            {availableRoles.map((role) => (
                              <MenuItem key={role} value={role}>
                                <Checkbox checked={selectedRoles.indexOf(role) > -1} />
                                <ListItemText primary={role} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <IconButton
                          size="small"
                          onClick={handleSaveRoles}
                          disabled={rolesUpdateLoading}
                          sx={{ ml: 1, width: 'auto' }}
                        >
                          {rolesUpdateLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SaveIcon />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelRolesEdit}
                          disabled={rolesUpdateLoading}
                          sx={{ ml: 1, width: 'auto' }}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {(authData.customClaims?.roles || (authData.customClaims?.roles ? authData.customClaims.roles : [])).map((r) => (
                            <Chip key={r} label={r} color="secondary" size="small" />
                          ))}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={handleEditRoles}
                          sx={{ ml: 1, width: 'auto' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Other custom claims (exclude role/admin markers) */}
                {authData.customClaims && Object.keys(authData.customClaims).filter(c => !['roles','admin'].includes(c)).length > 0 && (
                  <Grid item xs={12}>
                    <Box className="info-item">
                      <Typography className="info-label">Custom Claims</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {Object.entries(authData.customClaims).filter(([claim]) => !['roles','admin'].includes(claim)).map(([claim, value]) => (
                          <Chip 
                            key={claim}
                            label={`${claim}: ${value}`}
                            color="secondary"
                            size="small"
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Alert severity="info">
                No authentication data available.
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Map Section */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnIcon />
              Lake Locations ({validLakes.length} locations)
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {validLakes.length > 0 ? (
              <Box className="map-container">
                <MapContainer
                  center={getMapCenter()}
                  zoom={validLakes.length === 1 ? 12 : 8}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {validLakes.map((lake, index) => (
                    <Marker 
                      key={index} 
                      position={[lake.latitude, lake.longitude]}
                      icon={lake.name === 'Current Location' ? greenIcon : defaultIcon}
                    >
                      <Popup>
                        <div>
                          <strong>{lake.name || 'Unnamed Lake'}</strong>
                          {lake.stationId && (
                            <div>Station ID: {lake.stationId}</div>
                          )}
                          <div>
                            Coordinates: {lake.latitude.toFixed(6)}, {lake.longitude.toFixed(6)}
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </Box>
            ) : (
              <Alert severity="info">
                No lake locations found for this user.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default UserDetails;
